import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ExternalLink, Search, Copy, Mail } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useColumnFilters, HeaderFilter } from "@/components/ColumnFilter";
import { InviteDialog } from "@/components/finance/InviteDialog";

const MANDATE_TONE = {
  Active: "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]", Pending: "border-[var(--warn)] text-[var(--warn)] bg-[var(--warn-bg)]",
  Restricted: "border-[#C2410C] text-[#C2410C] bg-[#FFEDD5]", Failed: "border-[var(--err)] text-[var(--err)] bg-[var(--err-bg)]",
  Cancelled: "border-[var(--line)] text-[var(--ink-2)] bg-[var(--bg-2)]", Expired: "border-[var(--line)] text-[var(--ink-2)] bg-[var(--bg-2)]",
  "No Mandate": "border-[var(--line)] text-[var(--ink-2)] bg-[var(--bg-2)]",
};
const PAY_TONE = { "Paid Out": "text-[var(--ok)]", Confirmed: "text-[var(--navy)]", Pending: "text-[var(--warn)]", Failed: "text-[var(--err)]", Cancelled: "text-[var(--ink-2)]" };
const dmy = (s) => (s ? s.split("-").reverse().join("/") : "—");
const COLS = {
  name: { value: (r) => r.name }, email: { value: (r) => r.email }, collection: { value: (r) => r.payment_collection },
  mandate: { value: (r) => r.mandate_status }, xero: { value: (r) => (r.xero_linked ? "Linked" : "Not linked") },
  paid_out: { value: (r) => r.last_paid_out_date, sort: (r) => r.last_paid_out_date || "" },
  pay_status: { value: (r) => r.last_payment_status },
};
const HEAD = [["name", "Customer"], ["email", "Email"], ["collection", "Payment Collection"], ["mandate", "Mandate Status"], ["xero", "Xero"], ["paid_out", "Last Paid Out"], ["pay_status", "Payment Status"]];

export function DirectDebitTab({ canAct }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/gocardless/customers").then((r) => setData(r.data)).catch(() => setData({ rows: [] })); }, []);
  const searched = useMemo(() => (data?.rows || []).filter((r) => !q || `${r.name} ${r.email}`.toLowerCase().includes(q.toLowerCase())), [data, q]);
  const ctl = useColumnFilters(searched, COLS, { key: "name", dir: "asc" });

  if (!data) return <div className="text-center py-12 text-[var(--ink-2)]">Loading…</div>;
  if (!data.configured && !data.rows.length) {
    return <div className="card-flat p-10 text-center" data-testid="dd-not-configured"><div className="overline mb-2">GoCardless not connected</div><div className="text-sm text-[var(--ink-2)]">Add your GoCardless access token to see customers, mandates and payments here.</div></div>;
  }
  const counts = data.rows.reduce((a, r) => ({ ...a, [r.mandate_status]: (a[r.mandate_status] || 0) + 1 }), {});

  return (
    <div data-testid="dd-tab">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" />
          <input data-testid="dd-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer or email" className="input-slim !pl-7 w-64 text-xs" />
        </div>
        <span className="text-[11px] font-mono-data text-[var(--ink-2)]" data-testid="dd-count">{ctl.view.length} customers · Active {counts.Active || 0} · Pending {counts.Pending || 0} · No mandate {counts["No Mandate"] || 0}</span>
        {ctl.anyActive && <button data-testid="dd-clear" onClick={ctl.clearAll} className="text-[11px] underline text-[var(--ink-2)]">Clear filters</button>}
        <span className="ml-auto text-[10px] text-[var(--ink-2)]" data-testid="dd-last-sync">GoCardless {data.environment} · last sync {data.last_sync_at ? new Date(data.last_sync_at).toLocaleString("en-GB") : "never"}</span>
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="tbl tbl-slim" style={{ minWidth: 1000 }}>
          <thead><tr>{HEAD.map(([k, l]) => <th key={k}><HeaderFilter label={l} colKey={k} rows={searched} cols={COLS} ctl={ctl} testid={`dd-hdr-${k}`} /></th>)}<th className="text-right">Actions</th></tr></thead>
          <tbody>
            {ctl.view.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-[var(--ink-2)]" data-testid="dd-empty">{data.rows.length ? "No customers match." : "No GoCardless customers yet — click Sync now."}</td></tr>}
            {ctl.view.map((r) => <Row key={r.customer_id} r={r} dashboard={data.dashboard} canAct={canAct} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ r: initial, dashboard, canAct }) {
  const [r, setR] = useState(initial);
  const [dlg, setDlg] = useState(false);
  const closeDlg = useCallback((v) => setDlg(v), []);
  const hasMandate = r.mandate_status === "Active" || r.mandate_status === "Pending";
  async function toggleReminders() {
    const enabled = !r.reminders_enabled;
    try { await api.patch(`/gocardless/customers/${r.customer_id}/reminders`, { enabled }); setR({ ...r, reminders_enabled: enabled }); toast.success(`Daily reminders ${enabled ? "on" : "off"} for ${r.name}`); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }
  async function invite() {
    try {
      const res = await api.post(`/gocardless/customers/${r.customer_id}/invite`, { send_email: false });
      await navigator.clipboard?.writeText(res.data.url).catch(() => {});
      toast.success("Invite link copied");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }
  return (
    <tr data-testid={`dd-row-${r.customer_id}`}>
      <td className="font-medium truncate max-w-[220px]" title={r.name}>{r.name}</td>
      <td className="truncate max-w-[220px]">{r.email || "—"}</td>
      <td data-testid={`dd-collection-${r.customer_id}`}>{r.payment_collection}</td>
      <td><span className={`text-[10px] px-1.5 py-0.5 rounded-sm border whitespace-nowrap ${MANDATE_TONE[r.mandate_status] || MANDATE_TONE["No Mandate"]}`} data-testid={`dd-mandate-${r.customer_id}`}>{r.mandate_status}</span></td>
      <td><span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${r.xero_linked ? "border-[#93C5FD] text-[#1D4ED8] bg-[#EFF6FF]" : "border-[var(--line)] text-[var(--ink-2)]"}`} data-testid={`dd-xero-${r.customer_id}`}>{r.xero_linked ? "Linked" : "Not linked"}</span></td>
      <td className="font-mono-data text-[10.5px]" data-testid={`dd-paidout-${r.customer_id}`}>{dmy(r.last_paid_out_date)}{r.last_paid_out_amount ? ` · £${r.last_paid_out_amount.toFixed(2)}` : ""}</td>
      <td className={PAY_TONE[r.last_payment_status] || "text-[var(--ink-2)]"} data-testid={`dd-paystatus-${r.customer_id}`}>{r.last_payment_status || "—"}</td>
      <td className="text-right whitespace-nowrap">
        {r.invited_at && !hasMandate && (
          <span className="inline-flex items-center gap-1.5 mr-2 text-[10px] text-[var(--ink-2)]" data-testid={`dd-invited-${r.customer_id}`}>
            Invited {new Date(r.invited_at).toLocaleDateString("en-GB")}{r.reminder_count ? ` · ${r.reminder_count} reminder${r.reminder_count > 1 ? "s" : ""}` : ""}
            {canAct && (
              <button data-testid={`dd-reminders-${r.customer_id}`} onClick={toggleReminders} title="Daily reminder at 09:00"
                className={`px-1.5 h-[18px] rounded-sm border text-[9.5px] font-semibold transition-colors ${r.reminders_enabled ? "border-[var(--ok)] text-[var(--ok)] bg-[var(--ok-bg)]" : "border-[var(--line)] text-[var(--ink-2)]"}`}>
                Reminders {r.reminders_enabled ? "On" : "Off"}
              </button>
            )}
          </span>
        )}
        {hasMandate || !canAct ? (
          <a href={`${dashboard}/customers/${r.customer_id}`} target="_blank" rel="noreferrer" data-testid={`dd-manage-${r.customer_id}`} className="inline-flex items-center gap-1 text-[10px] px-2 h-[20px] rounded-full border border-[var(--ink)] hover:bg-[var(--bg-2)]">Manage <ExternalLink size={10} /></a>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`dd-invite-${r.customer_id}`} className="inline-flex items-center gap-1 text-[10px] px-2 h-[20px] rounded-full border border-[var(--ink)] hover:bg-[var(--bg-2)]">Invite <ChevronDown size={10} /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="text-xs">
              <DropdownMenuItem data-testid={`dd-invite-email-${r.customer_id}`} disabled={!r.email} onClick={() => setDlg(true)}><Mail size={12} className="mr-2" /> Email invite…</DropdownMenuItem>
              <DropdownMenuItem data-testid={`dd-invite-copy-${r.customer_id}`} onClick={() => invite()}><Copy size={12} className="mr-2" /> Copy invite link</DropdownMenuItem>
              <DropdownMenuItem asChild><a href={`${dashboard}/customers/${r.customer_id}`} target="_blank" rel="noreferrer"><ExternalLink size={12} className="mr-2" /> Open in GoCardless</a></DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {dlg && <InviteDialog customer={r} open={dlg} onOpenChange={closeDlg} onSent={(u) => setR({ ...r, ...u })} />}
      </td>
    </tr>
  );
}
